import { useState } from "react";
import { Upload, ArrowUp, ArrowDown, X } from "lucide-react";
import { toast } from "sonner";
import { api, asset, formatApiError } from "@/lib/api";

const move = (arr, i, d) => {
  const j = i + d;
  if (j < 0 || j >= arr.length) return arr;
  const out = [...arr];
  [out[i], out[j]] = [out[j], out[i]];
  return out;
};

export const PagesField = ({ pages, onChange, label = "STRIP IMAGES IN READING ORDER", help = "Select several images at once. They're sorted by file name (e.g. 01.jpg, 02.jpg), and you can reorder them after.", button = "UPLOAD PANELS" }) => {
  const [status, setStatus] = useState(null);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    e.target.value = "";
    if (!files.length) return;
    let next = [...pages];
    for (let i = 0; i < files.length; i++) {
      setStatus(`UPLOADING ${i + 1}/${files.length}`);
      const fd = new FormData();
      fd.append("file", files[i]);
      try {
        const { data } = await api.post("/upload", fd);
        next = [...next, data.url];
        onChange(next);
      } catch (err) {
        toast.error(`${files[i].name}: ${formatApiError(err)}`);
      }
    }
    setStatus(null);
    toast.success(`${files.length} IMAGE(S) UPLOADED`);
  };

  return (
    <div data-testid="pages-field">
      <span className="block font-mono text-[11px] tracking-[0.25em] text-zinc-400 mb-2">{label} ({pages.length})</span>
      <p className="text-xs text-zinc-400 mb-3">{help}</p>
      <label data-testid="pages-upload-btn" className="inline-flex cursor-pointer items-center gap-2 px-4 py-2.5 bg-[#F5A524] text-black font-mono text-[11px] font-bold tracking-[0.2em] hover:bg-white transition-colors">
        <Upload size={13} /> {status || button}
        <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} disabled={!!status} />
      </label>
      <div data-lenis-prevent className="mt-3 space-y-2 max-h-[320px] overflow-y-auto pr-1">
        {pages.map((p, i) => (
          <div key={`${p}-${i}`} data-testid={`page-row-${i}`} className="flex items-center gap-3 bg-[#0B0C10] border border-[#F5A524]/20 p-2">
            <span className="font-mono text-xs text-[#F5A524] w-6 text-center">{i + 1}</span>
            <img src={asset(p)} alt="" className="w-10 h-14 object-cover object-top" />
            <span className="flex-1 min-w-0 truncate font-mono text-[11px] text-zinc-400">{p.split("/").pop()}</span>
            <button type="button" data-testid={`page-up-${i}`} onClick={() => onChange(move(pages, i, -1))} aria-label="Move up" className="p-2 text-zinc-300 hover:text-[#F5A524]"><ArrowUp size={14} /></button>
            <button type="button" data-testid={`page-down-${i}`} onClick={() => onChange(move(pages, i, 1))} aria-label="Move down" className="p-2 text-zinc-300 hover:text-[#F5A524]"><ArrowDown size={14} /></button>
            <button type="button" data-testid={`page-remove-${i}`} onClick={() => onChange(pages.filter((_, j) => j !== i))} aria-label="Remove" className="p-2 text-[#FF2A55] hover:bg-[#FF2A55] hover:text-black"><X size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};
